import React, { useState } from 'react';
import {
    Check,
    Users,
    Percent,
    Calendar,
    XCircle,
    ShieldCheck,
    Rocket,
    Headphones,
    Lock,
    Building2
} from 'lucide-react';
import PublicPageLayout from '../../layouts/PublicPageLayout';

export default function PricingPage() {
    const [billingCycle, setBillingCycle] = useState<'monthly' | 'annual'>('monthly');

    // Tarifs ajustés selon le cycle de facturation
    const starterPrice = billingCycle === 'monthly' ? 29 : 23;
    const businessPrice = billingCycle === 'monthly' ? 59 : 47;
    const enterprisePrice = billingCycle === 'monthly' ? 99 : 79;

    const guarantees = [
        {
            icon: <Calendar className="w-5 h-5 text-blue-600" />,
            title: '14 jours',
            subtitle: "d'essai gratuit"
        },
        {
            icon: <XCircle className="w-5 h-5 text-blue-600" />,
            title: 'Sans engagement',
            subtitle: 'Annulation à tout moment'
        },
        {
            icon: <ShieldCheck className="w-5 h-5 text-blue-600" />,
            title: 'Paiement sécurisé',
            subtitle: '100% sécurisé'
        },
        {
            icon: <Rocket className="w-5 h-5 text-blue-600" />,
            title: 'Mise en place rapide',
            subtitle: 'En moins de 5 minutes'
        },
        {
            icon: <Headphones className="w-5 h-5 text-blue-600" />,
            title: 'Support 24/7',
            subtitle: 'Par téléphone, chat et email'
        },
        {
            icon: <Lock className="w-5 h-5 text-blue-600" />,
            title: 'Données sécurisées',
            subtitle: 'Conforme RGPD'
        }
    ];

    return (
        <PublicPageLayout>
            <div className="min-h-screen bg-[#FBFDFF] py-10 lg:py-14">
                <div className="max-w-7xl mx-auto px-6 lg:px-12 space-y-10">

                    {/* ================= HERO SECTION ================= */}
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-center">
                        
                        {/* Gauche : Titre et Description */}
                        <div className="lg:col-span-8 space-y-4">
                            <span className="text-xs font-bold text-blue-600 tracking-wider uppercase">
                                TARIFS
                            </span>

                            <h1 className="text-3xl lg:text-4xl font-extrabold text-slate-900 leading-tight">
                                Des forfaits simples et transparents<br />
                                adaptés à votre <span className="text-blue-600">croissance.</span>
                            </h1>

                            <p className="text-slate-500 text-xs sm:text-sm leading-relaxed max-w-xl">
                                Choisissez le forfait qui correspond à vos besoins.<br />
                                Tous nos forfaits incluent <strong>14 jours d'essai gratuit</strong>, sans engagement.
                            </p>
                        </div>

                        {/* Droite : Badge / Bannière Économie 20% */}
                        <div className="lg:col-span-4 bg-blue-50/60 rounded-2xl p-4 border border-blue-100 flex items-center gap-3.5 shadow-sm">
                            <div className="w-10 h-10 rounded-xl bg-blue-600 text-white flex items-center justify-center shrink-0 shadow-md shadow-blue-500/20">
                                <Percent size={20} />
                            </div>
                            <div>
                                <h4 className="text-xs font-extrabold text-blue-600">Économisez 20%</h4>
                                <p className="text-[11px] text-slate-600 leading-tight">
                                    avec la facturation annuelle et bénéficiez de <strong>2 mois offerts</strong>.
                                </p>
                            </div>
                        </div>

                    </div>

                    {/* ================= TOGGLE MENSUEL / ANNUEL ================= */}
                    <div className="flex items-center gap-2">
                        <div className="bg-slate-100 p-1 rounded-xl flex items-center gap-1 border border-slate-200/60">
                            <button
                                onClick={() => setBillingCycle('monthly')}
                                className={`px-4 py-1.5 rounded-lg text-xs font-bold transition-all ${
                                    billingCycle === 'monthly'
                                        ? 'bg-blue-600 text-white shadow-sm'
                                        : 'text-slate-600 hover:text-slate-900'
                                }`}
                            >
                                Mensuel
                            </button>
                            <button
                                onClick={() => setBillingCycle('annual')}
                                className={`px-4 py-1.5 rounded-lg text-xs font-bold transition-all ${
                                    billingCycle === 'annual'
                                        ? 'bg-blue-600 text-white shadow-sm'
                                        : 'text-slate-600 hover:text-slate-900'
                                }`}
                            >
                                Annuel
                            </button>
                        </div>
                        <span className="bg-amber-100 text-amber-700 text-[10px] font-extrabold px-2 py-1 rounded-lg">
                            -20%
                        </span>
                    </div>

                    {/* ================= GRILLE DES TARIFS (4 COLONNES) ================= */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 items-stretch">

                        {/* 1. STARTER */}
                        <div className="bg-white rounded-3xl p-6 border border-slate-200/80 shadow-sm flex flex-col justify-between space-y-6">
                            <div className="space-y-4">
                                <div>
                                    <h3 className="text-base font-extrabold text-slate-900">Starter</h3>
                                    <p className="text-[11px] text-slate-500 mt-1 min-h-[32px]">
                                        Idéal pour les petites équipes qui débutent.
                                    </p>
                                </div>

                                <div className="space-y-0.5">
                                    <div className="flex items-baseline gap-1">
                                        <span className="text-3xl font-black text-slate-900">{starterPrice} $</span>
                                        <span className="text-xs text-slate-400 font-medium">/ agent / mois</span>
                                    </div>
                                    <p className="text-[10px] text-slate-400">
                                        {billingCycle === 'monthly' ? 'Facturé mensuellement' : 'Facturé annuellement'}
                                    </p>
                                </div>

                                <button className="w-full py-2.5 rounded-xl border border-blue-600 text-blue-600 font-bold text-xs hover:bg-blue-50 transition-all">
                                    Commencer
                                </button>

                                <ul className="space-y-2.5 pt-2 text-[11px] text-slate-600">
                                    <li className="flex items-center gap-2">
                                        <Check size={14} className="text-blue-600 shrink-0" />
                                        <span>Appels entrants et sortants</span>
                                    </li>
                                    <li className="flex items-center gap-2">
                                        <Check size={14} className="text-blue-600 shrink-0" />
                                        <span>IVR de base</span>
                                    </li>
                                    <li className="flex items-center gap-2">
                                        <Check size={14} className="text-blue-600 shrink-0" />
                                        <span>Boîte vocale</span>
                                    </li>
                                    <li className="flex items-center gap-2">
                                        <Check size={14} className="text-blue-600 shrink-0" />
                                        <span>Enregistrement des appels</span>
                                    </li>
                                    <li className="flex items-center gap-2">
                                        <Check size={14} className="text-blue-600 shrink-0" />
                                        <span>1 numéro local inclus</span>
                                    </li>
                                    <li className="flex items-center gap-2">
                                        <Check size={14} className="text-blue-600 shrink-0" />
                                        <span>Rapports de base</span>
                                    </li>
                                    <li className="flex items-center gap-2">
                                        <Check size={14} className="text-blue-600 shrink-0" />
                                        <span>Support par email</span>
                                    </li>
                                </ul>
                            </div>

                            <div className="bg-slate-50/80 rounded-xl p-2.5 flex items-center gap-2 text-[11px] text-slate-600 font-medium">
                                <Users size={14} className="text-blue-600 shrink-0" />
                                <span>Jusqu'à 5 agents</span>
                            </div>
                        </div>

                        {/* 2. BUSINESS (Populaire) */}
                        <div className="bg-white rounded-3xl p-6 border-2 border-blue-600 shadow-md relative flex flex-col justify-between space-y-6">
                            {/* Badge Le plus populaire */}
                            <div className="absolute -top-3.5 left-1/2 -translate-x-1/2 bg-blue-600 text-white text-[10px] font-extrabold px-3 py-1 rounded-full shadow-sm">
                                Le plus populaire
                            </div>

                            <div className="space-y-4 pt-1">
                                <div>
                                    <h3 className="text-base font-extrabold text-blue-600">Business</h3>
                                    <p className="text-[11px] text-slate-500 mt-1 min-h-[32px]">
                                        Parfait pour les équipes en croissance qui veulent plus de puissance.
                                    </p>
                                </div>

                                <div className="space-y-0.5">
                                    <div className="flex items-baseline gap-1">
                                        <span className="text-3xl font-black text-slate-900">{businessPrice} $</span>
                                        <span className="text-xs text-slate-400 font-medium">/ agent / mois</span>
                                    </div>
                                    <p className="text-[10px] text-slate-400">
                                        {billingCycle === 'monthly' ? 'Facturé mensuellement' : 'Facturé annuellement'}
                                    </p>
                                </div>

                                <button className="w-full py-2.5 rounded-xl bg-blue-600 text-white font-bold text-xs hover:bg-blue-700 transition-all shadow-md shadow-blue-500/20">
                                    Commencer
                                </button>

                                <div className="text-[11px] font-extrabold text-blue-600 pt-1">
                                    Tout dans Starter, plus :
                                </div>

                                <ul className="space-y-2.5 text-[11px] text-slate-600">
                                    <li className="flex items-center gap-2">
                                        <Check size={14} className="text-blue-600 shrink-0" />
                                        <span>IVR avancé</span>
                                    </li>
                                    <li className="flex items-center gap-2">
                                        <Check size={14} className="text-blue-600 shrink-0" />
                                        <span>Files d'attente intelligentes</span>
                                    </li>
                                    <li className="flex items-center gap-2">
                                        <Check size={14} className="text-blue-600 shrink-0" />
                                        <span>WhatsApp Business</span>
                                    </li>
                                    <li className="flex items-center gap-2">
                                        <Check size={14} className="text-blue-600 shrink-0" />
                                        <span>SMS illimités</span>
                                    </li>
                                    <li className="flex items-center gap-2">
                                        <Check size={14} className="text-blue-600 shrink-0" />
                                        <span>Intégration CRM</span>
                                    </li>
                                    <li className="flex items-center gap-2">
                                        <Check size={14} className="text-blue-600 shrink-0" />
                                        <span>Rapports avancés</span>
                                    </li>
                                    <li className="flex items-center gap-2">
                                        <Check size={14} className="text-blue-600 shrink-0" />
                                        <span>Support prioritaire 24/7</span>
                                    </li>
                                </ul>
                            </div>

                            <div className="bg-blue-50/80 rounded-xl p-2.5 flex items-center gap-2 text-[11px] text-blue-700 font-medium">
                                <Users size={14} className="text-blue-600 shrink-0" />
                                <span>Jusqu'à 50 agents</span>
                            </div>
                        </div>

                        {/* 3. ENTERPRISE */}
                        <div className="bg-white rounded-3xl p-6 border border-slate-200/80 shadow-sm flex flex-col justify-between space-y-6">
                            <div className="space-y-4">
                                <div>
                                    <h3 className="text-base font-extrabold text-slate-900">Enterprise</h3>
                                    <p className="text-[11px] text-slate-500 mt-1 min-h-[32px]">
                                        Pour les grandes entreprises avec des besoins avancés.
                                    </p>
                                </div>

                                <div className="space-y-0.5">
                                    <div className="flex items-baseline gap-1">
                                        <span className="text-3xl font-black text-slate-900">{enterprisePrice} $</span>
                                        <span className="text-xs text-slate-400 font-medium">/ agent / mois</span>
                                    </div>
                                    <p className="text-[10px] text-slate-400">
                                        {billingCycle === 'monthly' ? 'Facturé mensuellement' : 'Facturé annuellement'}
                                    </p>
                                </div>

                                <button className="w-full py-2.5 rounded-xl border border-blue-600 text-blue-600 font-bold text-xs hover:bg-blue-50 transition-all">
                                    Commencer
                                </button>

                                <div className="text-[11px] font-extrabold text-blue-600 pt-1">
                                    Tout dans Business, plus :
                                </div>

                                <ul className="space-y-2.5 text-[11px] text-slate-600">
                                    <li className="flex items-center gap-2">
                                        <Check size={14} className="text-blue-600 shrink-0" />
                                        <span>Intégrations avancées (API)</span>
                                    </li>
                                    <li className="flex items-center gap-2">
                                        <Check size={14} className="text-blue-600 shrink-0" />
                                        <span>Routage basé sur les compétences</span>
                                    </li>
                                    <li className="flex items-center gap-2">
                                        <Check size={14} className="text-blue-600 shrink-0" />
                                        <span>Tableaux de bord personnalisés</span>
                                    </li>
                                    <li className="flex items-center gap-2">
                                        <Check size={14} className="text-blue-600 shrink-0" />
                                        <span>IA Gaston (Assistant intelligent)</span>
                                    </li>
                                    <li className="flex items-center gap-2">
                                        <Check size={14} className="text-blue-600 shrink-0" />
                                        <span>Sécurité avancée & SSO</span>
                                    </li>
                                    <li className="flex items-center gap-2">
                                        <Check size={14} className="text-blue-600 shrink-0" />
                                        <span>Gestion avancée des rôles</span>
                                    </li>
                                    <li className="flex items-center gap-2">
                                        <Check size={14} className="text-blue-600 shrink-0" />
                                        <span>Account manager dédié</span>
                                    </li>
                                </ul>
                            </div>

                            <div className="bg-slate-50/80 rounded-xl p-2.5 flex items-center gap-2 text-[11px] text-slate-600 font-medium">
                                <Users size={14} className="text-blue-600 shrink-0" />
                                <span>Jusqu'à 200 agents</span>
                            </div>
                        </div>

                        {/* 4. SUR-MESURE (SOMBRE) */}
                        <div className="bg-secondary text-white rounded-3xl p-6 shadow-xl flex flex-col justify-between space-y-6">
                            <div className="space-y-5">
                                <div className="space-y-2">
                                    <div className="w-9 h-9 rounded-xl bg-amber-500/20 text-amber-400 flex items-center justify-center shrink-0">
                                        <Building2 size={20} />
                                    </div>
                                    <h3 className="text-base font-extrabold text-amber-400">Sur-mesure</h3>
                                    <p className="text-[11px] text-slate-300 leading-relaxed">
                                        Une solution entièrement adaptée à vos besoins spécifiques.
                                    </p>
                                </div>

                                <div className="border-t border-slate-700/80 pt-4 space-y-1">
                                    <h4 className="text-sm font-bold text-white">Tarif personnalisé</h4>
                                    <p className="text-[10px] text-slate-400">
                                        Contactez-nous pour une offre adaptée à votre organisation.
                                    </p>
                                </div>

                                <button className="w-full py-2.5 rounded-xl bg-amber-500 hover:bg-amber-600 text-slate-950 font-bold text-xs transition-all shadow-md shadow-amber-500/20">
                                    Demander une démo
                                </button>

                                <ul className="space-y-2.5 pt-2 text-[11px] text-slate-200">
                                    <li className="flex items-center gap-2">
                                        <Check size={14} className="text-amber-400 shrink-0" />
                                        <span>Déploiement dédié</span>
                                    </li>
                                    <li className="flex items-center gap-2">
                                        <Check size={14} className="text-amber-400 shrink-0" />
                                        <span>Intégrations sur-mesure</span>
                                    </li>
                                    <li className="flex items-center gap-2">
                                        <Check size={14} className="text-amber-400 shrink-0" />
                                        <span>SLA personnalisé</span>
                                    </li>
                                    <li className="flex items-center gap-2">
                                        <Check size={14} className="text-amber-400 shrink-0" />
                                        <span>Formation dédiée</span>
                                    </li>
                                    <li className="flex items-center gap-2">
                                        <Check size={14} className="text-amber-400 shrink-0" />
                                        <span>Support premium</span>
                                    </li>
                                </ul>
                            </div>
                        </div>

                    </div>

                    {/* ================= GARANTIES & SÉCURITÉ (BARRE DU BAS) ================= */}
                    <div className="bg-white rounded-2xl p-4 lg:px-6 border border-slate-200/70 shadow-sm grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                        {guarantees.map((item, idx) => (
                            <div key={idx} className="flex items-center gap-3">
                                <div className="shrink-0">{item.icon}</div>
                                <div>
                                    <h5 className="text-xs font-bold text-slate-900 leading-tight">{item.title}</h5>
                                    <p className="text-[10px] text-slate-500 leading-tight">{item.subtitle}</p>
                                </div>
                            </div>
                        ))}
                    </div>

                </div>
            </div>
        </PublicPageLayout>
    );
}